import styles from './ui-menu-tab.css';
import { Component } from '../../../decorators/component.decorator';
import { Attribute } from '../../../decorators/attribute.decorator';
import { Child, Children } from '../../../decorators/child.decorator';
import { UIElement } from '../../ui-element';
import { UIMenuColumn } from './ui-menu-column';
import { UIIcon } from '../../icon/ui-icon';
import { MaybeHTMLElement } from '../../../global/utils.type';
import { insertElements } from '../../../global/render-api';
import { dispatchMenuClick } from '../menu-click.helpers';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIMenuTab
 * @element ui-menu-tab
 * @classdesc Represents a class for <code>ui-menu-tab</code> element. Should be used inside <code>ui-menu</code> element.
 * @property {string} label {@attr label} The menu tab's label text
 * @property {string} [glyph] {@attr glyph} Glyph icon to the left of the label.
 * @property {string} [href] {@attr href} When set, the tab renders as a navigation link instead of a menu toggle button.
 * No submenu columns are shown and the menu will not open/close when the tab is clicked.
 * @property {boolean} [expanded=false] {@attr expanded} Whether this tab's submenu is currently expanded (open). Only the expanded tab's items are visible when the menu is open.
 * @property {boolean} [current=false] {@attr current} Whether the user is on a page corresponding to one of this tab's menu items.
 * @property {HTMLButtonElement} button Read-only. Shortcut to the child button element. Only present when the tab is not a link.
 * @property {HTMLAnchorElement} anchor Read-only. Shortcut to the child anchor element. Only present when the tab has an <code>href</code> attribute.
 * @property {UIIcon} [chevron] Read-only. Shortcut to optional child chevron icon element visible only on desktop when chevron is enabled.
 * @property {HTMLElement} [icon] Read-only. Shortcut to optional icon element to the left of the label.
 * @fires event:menu-click Dispatched when the tab anchor is clicked in link mode (when <code>href</code> is set).
 * Bubbles and is composed. The event detail contains <code>{ href }</code>, consistent with <code>ui-menu-link</code>.
 * @example
 * <ui-menu-tab label="Finance" glyph="redesign-line-growth-arrow"></ui-menu-tab>
 * @example
 * <ui-menu-tab label="Home" href="/home"></ui-menu-tab>
 */
@Component({
    tag: 'ui-menu-tab',
    styles,
})
export class UIMenuTab extends UIElement {
    @Attribute() label!: string;
    @Attribute() glyph?: string;
    @Attribute() href?: string;
    @Attribute(Boolean) expanded: boolean = false;
    @Attribute(Boolean) current: boolean = false;

    @Child('.ui-menu-tab__button') button?: HTMLButtonElement;
    @Child('.ui-menu-tab__anchor') anchor?: HTMLAnchorElement;
    @Child('.ui-menu-tab__chevron') chevron?: UIIcon;
    @Child('.ui-menu-tab__icon') icon?: MaybeHTMLElement;
    @Children('ui-menu-column') menuColumns!: Array<UIMenuColumn>;

    private observer!: MutationObserver;

    /** Attributes that, when changed at runtime, require re-render or ARIA sync. */
    private static readonly OBSERVED_ATTRIBUTES = [
        'expanded',
        'current',
        'label',
        'glyph',
        'href',
    ];

    /** Returns true when the tab is configured as a navigation link. */
    get isLink(): boolean {
        return !!this.href;
    }

    /**
     * The focusable inner element — either the anchor (link mode) or the button.
     */
    get focusTarget(): HTMLElement | undefined {
        return this.anchor ?? this.button ?? undefined;
    }

    /**
     * Returns the appropriate chevron glyph based on the expanded state.
     * @private
     */
    private get expandedChevron(): string {
        return this.expanded ? 'redesign-chevron-up' : 'redesign-chevron-down';
    }

    /**
     * Creates the optional leading glyph icon for the tab label.
     * @private
     * @returns {MaybeHTMLElement} <ui-icon> element or null when no glyph is provided.
     */
    private createIcon(): MaybeHTMLElement {
        if (!this.glyph) {
            return null;
        }
        return this.createElement({
            tagName: 'ui-icon',
            classList: {
                'ui-menu-tab__icon': true,
            },
            attributes: {
                glyph: this.glyph,
            },
        });
    }

    /**
     * Creates either the button or anchor inside the tab depending on the layout.
     * @private
     * @returns {HTMLElement} element.
     */
    private createInteractiveElement(): HTMLElement {
        const isLink = this.isLink;

        return this.createElement({
            tagName: isLink ? 'a' : 'button',
            classList: {
                [isLink ? 'ui-menu-tab__anchor' : 'ui-menu-tab__button']: true,
            },
            attributes: {
                ...(isLink
                    ? { href: this.href! }
                    : {
                          'aria-haspopup': 'menu',
                          'aria-expanded': this.expanded,
                      }),
                'role': 'menuitem',
                'aria-current': this.current ? 'page' : null,
            },
            children: [this.label],
        });
    }

    /**
     * Creates the chevron icon that reflects the current expanded state on desktop.
     * @private
     * @returns {HTMLElement} element
     */
    private createChevron(): HTMLElement {
        return this.createElement({
            tagName: 'ui-icon',
            classList: {
                'ui-menu-tab__chevron': true,
            },
            attributes: {
                glyph: this.expandedChevron,
            },
        });
    }

    /**
     * Handles observed attribute mutations by keeping UI in sync.
     * Content attributes (label/glyph/href) rebuild the inner markup; state
     * attributes (expanded/current) only patch ARIA to avoid a full re-render.
     * @private
     */
    private handleMutations(mutations: Array<MutationRecord>): void {
        const changed = new Set(
            mutations.map((mutation) => mutation.attributeName)
        );

        if (
            changed.has('label') ||
            changed.has('glyph') ||
            changed.has('href')
        ) {
            this.render();
            return;
        }

        if (this.isLink) {
            if (this.current) {
                this.anchor?.setAttribute('aria-current', 'page');
            } else {
                this.anchor?.removeAttribute('aria-current');
            }
            return;
        }

        this.chevron?.setAttribute('glyph', this.expandedChevron);
        this.button?.setAttribute('aria-expanded', String(this.expanded));

        if (this.button) {
            if (this.current) {
                this.button.setAttribute('aria-current', 'page');
            } else {
                this.button.removeAttribute('aria-current');
            }
        }
    }

    /**
     * Handles link activation from the tab host so it remains wired after inner rerenders.
     * @fires event:menu-click
     * @private
     */
    private handleLinkClick = (event: Event): void => {
        if (!this.isLink || !this.anchor?.contains(event.target as Node)) {
            return;
        }

        if (dispatchMenuClick(this, this.href, event)) {
            event.preventDefault();
        }
    };

    /**
     * @inheritDoc
     */
    render(): void {
        this.setAttribute('role', 'none');
        this.querySelectorAll(':scope > .ui-menu-tab__wrapper').forEach(
            (wrapper) => wrapper.remove()
        );

        const innerElement = this.createInteractiveElement();
        const children = this.isLink
            ? [this.createIcon(), innerElement]
            : [this.createIcon(), innerElement, this.createChevron()];

        insertElements(this, [
            {
                tagName: 'div',
                classList: {
                    'ui-menu-tab__wrapper': true,
                },
                children,
            },
        ]);
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.observer = new MutationObserver(this.handleMutations.bind(this));
        this.observer.observe(this, {
            attributes: true,
            attributeFilter: UIMenuTab.OBSERVED_ATTRIBUTES,
        });
        this.addEventListener('click', this.handleLinkClick);
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.observer.disconnect();
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.observer.observe(this, {
            attributes: true,
            attributeFilter: UIMenuTab.OBSERVED_ATTRIBUTES,
        });
    }
}
